import React, { useEffect, useState } from "react";
import SummaryApi from "../common";
import { IoAddCircleOutline, IoCalendarOutline, IoTimeOutline, IoChevronDown, IoChevronUp, IoChevronForwardOutline ,IoSparklesOutline,IoArrowForwardOutline} from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { encodeCourseId } from "../utils/encoder";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const ITEMS_PER_PAGE = 10;

const InstructorCourseschedule = () => {
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState([]);
  const [filteredSchedule, setFilteredSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterBatch, setFilterBatch] = useState("");
  const [filterCourse, setFilterCourse] = useState("All");
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedCourses, setExpandedCourses] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second for live timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const normalize = (d) => {
    const x = new Date(d);
    return new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  };

  const matchSelectedStart = (slotStart, selectedStart) => {
    if (!selectedStart) return true;
    return normalize(slotStart) >= normalize(selectedStart);
  };

  const overlapOrBetween = (slotStart, slotEnd, selectedStart, selectedEnd) => {
    const s = normalize(slotStart);
    const e = normalize(slotEnd);
    const fs = selectedStart ? normalize(selectedStart) : null;
    const fe = selectedEnd ? normalize(selectedEnd) : null;

    if (!fs && !fe) return true;

    const overlaps =
      (!fe || s <= fe) &&
      (!fs || e >= fs);

    const fullyInside =
      (!fs || s >= fs) &&
      (!fe || e <= fe);

    return overlaps || fullyInside;
  };

  // Get current day name (monday, tuesday, etc.)
  const getCurrentDay = () => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[currentTime.getDay()];
  };

  // Extract time from time range string
  const extractTimeFromRange = (timeRange) => {
    if (!timeRange || timeRange === "N/A") return { startTime: null, endTime: null };
    const times = timeRange.split('-');
    return {
      startTime: times[0]?.trim(),
      endTime: times[1]?.trim()
    };
  };

  // Format milliseconds to readable time
  const formatCountdown = (ms) => {
    if (ms <= 0) return "0s";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${h > 0 ? h + "h " : ""}${m > 0 ? m + "m " : ""}${s}s`;
  };

  // Get timer status for a time slot
  const getTimerStatus = (slot) => {
    const currentDay = getCurrentDay();
    const timeRange = slot.days[currentDay];
    
    if (!timeRange || timeRange === "N/A") {
      return {
        text: "No class today",
        color: "text-gray-500 dark:text-gray-400",
        bgColor: "bg-gray-100 dark:bg-gray-800"
      };
    }

    const { startTime, endTime } = extractTimeFromRange(timeRange);
    if (!startTime || !endTime) {
      return {
        text: "Invalid time",
        color: "text-gray-500 dark:text-gray-400",
        bgColor: "bg-gray-100 dark:bg-gray-800"
      };
    }

    const now = currentTime;
    const today = new Date().toISOString().split('T')[0];
    const startDateTime = new Date(`${today}T${startTime}`);
    const endDateTime = new Date(`${today}T${endTime}`);

    if (now < startDateTime) {
      const timeRemaining = startDateTime - now;
      return {
        text: `Starts in: ${formatCountdown(timeRemaining)}`,
        color: "text-blue-600 dark:text-blue-400",
        bgColor: "bg-blue-50 dark:bg-blue-900/20"
      };
    } else if (now >= endDateTime) {
      return {
        text: "Completed",
        color: "text-red-600 dark:text-red-400",
        bgColor: "bg-red-50 dark:bg-red-900/20"
      };
    } else if (now >= startDateTime && now < endDateTime) {
      const timeRemaining = endDateTime - now;
      return {
        text: `Ongoing: ${formatCountdown(timeRemaining)} left`,
        color: "text-green-600 dark:text-green-400",
        bgColor: "bg-green-50 dark:bg-green-900/20"
      };
    }

    return {
      text: "Unknown status",
      color: "text-gray-500 dark:text-gray-400",
      bgColor: "bg-gray-100 dark:bg-gray-800"
    };
  };

  // Format time range for display
  const formatTimeRange = (timeRange) => {
    if (!timeRange || timeRange === "N/A") return "N/A";
    const times = timeRange.split('-');
    const formatTime = (timeStr) => {
      const [h, m] = timeStr.split(':').slice(0, 2);
      const hour = parseInt(h, 10);
      const period = hour >= 12 ? 'PM' : 'AM';
      const adjHour = hour % 12 || 12;
      return `${adjHour}:${m} ${period}`;
    };
    return `${formatTime(times[0])} - ${formatTime(times[1])}`;
  };

  useEffect(() => {
    const instructorId = Number(sessionStorage.getItem("instructor_id"));
    if (!instructorId) {
      console.error("No instructor ID found in session.");
      setLoading(false);
      return;
    }

    fetch(SummaryApi.instructorTimeslotByInstructorId.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ instructor_id: instructorId }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          const transformedData = transformApiData(data.data);
          setSchedule(transformedData);
        } else {
          console.error("Invalid response from API", data);
        }
      })
      .catch((err) => {
        console.error("Error fetching timeslots:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (schedule.length > 0 && Object.keys(expandedCourses).length === 0) {
      setExpandedCourses({ [schedule[0].course_id]: true });
    }
  }, [schedule]);

  const transformApiData = (apiData) => {
    const groupedByCourse = {};
    apiData.forEach((slot) => {
      const courseId = slot.course_id;
      if (!groupedByCourse[courseId]) {
        groupedByCourse[courseId] = {
          course_id: slot.course_id,
          course_title: slot.course_title,
          category_id: slot.category_id,
          category_name: slot.category_name,
          course_image1: slot.course_image1,
          time_slots: [],
        };
      }
      groupedByCourse[courseId].time_slots.push({
        time_slot_id: slot.time_slot_id,
        instructor_id: slot.instructor_id,
        batch_name: slot.batch_name || "N/A",
        start_date: slot.start_date,
        end_date: slot.end_date,
        is_course_completed: slot.is_course_completed,
        is_active: slot.is_active,
        created_date: slot.created_date,
        days: {
          sunday: slot.sunday,
          monday: slot.monday,
          tuesday: slot.tuesday,
          wednesday: slot.wednesday,
          thursday: slot.thursday,
          friday: slot.friday,
          saturday: slot.saturday,
        },
      });
    });
    return Object.values(groupedByCourse);
  };

  const courseTitles = Array.from(
    new Set(schedule.map((item) => item.course_title))
  );

  useEffect(() => {
    let filtered = [...schedule];

    if (filterCourse !== "All") {
      filtered = filtered.filter(
        (item) =>
          item.course_title?.toLowerCase() === filterCourse.toLowerCase()
      );
    }

    if (filterBatch) {
      filtered = filtered
        .map((course) => ({
          ...course,
          time_slots: course.time_slots.filter((slot) =>
            (slot.batch_name || "N/A")
              .toLowerCase()
              .includes(filterBatch.toLowerCase())
          ),
        }))
        .filter((course) => course.time_slots.length > 0);
    }

    filtered = filtered
      .map((course) => ({
        ...course,
        time_slots: course.time_slots.filter((slot) => {
          const startMatch = matchSelectedStart(
            slot.start_date,
            filterStartDate
          );

          const overlapMatch = overlapOrBetween(
            slot.start_date,
            slot.end_date,
            filterStartDate,
            filterEndDate
          );

          return startMatch && overlapMatch;
        }),
      }))
      .filter((course) => course.time_slots.length > 0);

    setFilteredSchedule(filtered);
    setCurrentPage(1);
  }, [schedule, filterBatch, filterCourse, filterStartDate, filterEndDate]);

  const handleSlotClick = (courseId, slot) => {
    const encodedId = encodeCourseId(courseId);
    navigate(
      `/instructor-panel/curriculum/${encodedId}?time_slot_id=${
        slot.time_slot_id
      }&batch_name=${encodeURIComponent(slot.batch_name || "N/A")}`
    );
  };

  const toggleCourse = (courseId) => {
    setExpandedCourses((prev) => ({
      ...prev,
      [courseId]: !prev[courseId],
    }));
  };

  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedSchedule = filteredSchedule.slice(
    startIdx,
    startIdx + ITEMS_PER_PAGE
  );
  const totalPages = Math.ceil(filteredSchedule.length / ITEMS_PER_PAGE);

  const handlePageChange = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
    }
  };

  return (
<div className="p-4 sm:p-8 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
  <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
    <h2 className="text-xl sm:text-2xl font-bold text-[#1f4774] dark:text-white mb-2 sm:mb-0">
      Course Schedule
    </h2>
    <a
      href="/instructor-panel/addCourseSchedule"
      className="bg-[#0E3A6D] dark:bg-[#0E3A6D] p-3 rounded text-white flex items-center text-[16px] hover:bg-blue-600 dark:hover:bg-blue-600 transition-colors duration-300"
    >
      <IoAddCircleOutline className="mr-1 text-[20px]" />
      Add Slots
    </a>
  </div>

  {/* Filters Section */}
  <div className="flex flex-wrap gap-4 mb-4 items-end">
    <div>
      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
        Search by Batch
      </label>
      <input
        type="text"
        value={filterBatch}
        onChange={(e) => setFilterBatch(e.target.value)}
        className="border border-gray-300 dark:border-gray-600 px-3 py-3 rounded w-full sm:w-48 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1f4774] dark:focus:ring-blue-400 transition-colors duration-200"
        placeholder="Search batch name..."
      />
    </div>
    <div>
      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
        Filter by Course
      </label>
      <select
        value={filterCourse}
        onChange={(e) => setFilterCourse(e.target.value)}
        className="border border-gray-300 dark:border-gray-600 px-3 py-3 rounded w-full sm:w-48 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1f4774] dark:focus:ring-blue-400 transition-colors duration-200"
      >
        <option value="All">All</option>
        {courseTitles.map((title) => (
          <option key={title} value={title}>
            {title}
          </option>
        ))}
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
        Start Date
      </label>
      <DatePicker
        selected={filterStartDate}
        onChange={(date) => setFilterStartDate(date)}
        className="border border-gray-300 dark:border-gray-600 px-3 py-3 rounded w-full sm:w-48 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1f4774] dark:focus:ring-blue-400 transition-colors duration-200"
        placeholderText="dd-mm-yyyy"
        dateFormat="dd-MM-yyyy"
      />
    </div>
    <div>
      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
        End Date
      </label>
      <DatePicker
        selected={filterEndDate}
        onChange={(date) => setFilterEndDate(date)}
        className="border border-gray-300 dark:border-gray-600 px-3 py-3 rounded w-full sm:w-48 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1f4774] dark:focus:ring-blue-400 transition-colors duration-200"
        placeholderText="dd-mm-yyyy"
        dateFormat="dd-MM-yyyy"
      />
    </div>
    <div>
      <button
        onClick={() => {
          setFilterBatch("");
          setFilterCourse("All");
          setFilterStartDate(null);
          setFilterEndDate(null);
        }}
        className="bg-gray-200 dark:bg-gray-700 px-4 py-3 rounded text-sm w-full sm:w-auto text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
      >
        Reset Filters
      </button>
    </div>
  </div>

  {/* Schedule Table */}
  {loading ? (
    <p className="text-gray-600 dark:text-gray-400">Loading schedule...</p>
  ) : (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg shadow dark:shadow-gray-800">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
        <thead className="bg-gray-100 dark:bg-gray-800">
          <tr>
            <th className="px-4 py-2 text-start align-middle text-gray-700 dark:text-gray-300">
              Course Name
            </th>
            <th className="px-4 py-2 text-center align-middle text-gray-700 dark:text-gray-300">
              Action
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {filteredSchedule.length === 0 ? (
            <tr>
              <td colSpan="2" className="px-4 py-16 text-center text-gray-500 dark:text-gray-400 text-lg py-16">
                No courses found for the selected filters.
              </td>
            </tr>
          ) : (
            paginatedSchedule.map((course) => (
              <React.Fragment key={course.course_id}>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                  <td
                    className="px-6 py-6 font-bold cursor-pointer text-gray-900 dark:text-white flex items-center gap-5"
                    onClick={() => toggleCourse(course.course_id)}
                  >
                    <img
                      src={
                        course.course_image1
                          ? `${SummaryApi.instructorCourseImage.url.replace("api", "")}${course.course_image1.replace("/api", "")}`
                          : "/default-course.jpg"
                      }
                      alt={course.course_title}
                      className="w-20 h-20 rounded-xl object-cover border-2 border-gray-200 dark:border-gray-600 shadow-md"
                    />
                    <span className="text-lg">{course.course_title}</span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <button
                      onClick={() => toggleCourse(course.course_id)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 font-medium flex items-center mx-auto gap-1"
                    >
                      {expandedCourses[course.course_id] ? <IoChevronUp /> : <IoChevronDown />}
                      {expandedCourses[course.course_id] ? "Hide" : "Show"} Details
                    </button>
                  </td>
                </tr>

                {/* BEAUTIFUL, NEAT & IMPRESSIVE TIME SLOT CARDS */}
                {expandedCourses[course.course_id] && (
                  <tr>
                    <td colSpan="2" className="px-6 py-10 bg-gray-50 dark:bg-gray-900">
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7 max-w-7xl mx-auto">
                        {course.time_slots.map((slot) => {
                          const timerStatus = getTimerStatus(slot);
                          const currentDay = getCurrentDay();
                          const todayTimeRange = slot.days[currentDay];

                          return (
                            <div
                              key={slot.time_slot_id}
                              onClick={() => handleSlotClick(course.course_id, slot)}
                              className="group relative bg-white dark:bg-gray-800 rounded-2xl border-2 border-transparent hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl overflow-hidden"
                            >
                              {/* Top Accent Bar */}

                              <div className="p-6">
                                {/* Batch Name + Completed Badge */}
                                <div className="flex justify-center items-center mb-4">
                                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {slot.batch_name}
                                  </h3>
                                  {slot.is_course_completed === 1 && (
                                    <span className="px-3 py-1 text-sm font-bold text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-full shadow-md">
                                      Completed
                                    </span>
                                  )}
                                </div>

                                {/* Status Badge */}
                                <div className={`inline-flex items-center gap-2 px-5 py-1 rounded-full text-sm font-semibold mb-5 ${timerStatus.color} ${timerStatus.bgColor} shadow-sm`}>
                                  <IoTimeOutline className="text-lg" />
                                  {timerStatus.text}
                                </div>

                                {/* Date Range */}
                                <div className="flex items-center justify-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-5">
                                  <div className="flex items-center gap-2">
                                    <IoCalendarOutline className="text-green-600 dark:text-green-400" />
                                    <span>{slot.start_date ? new Date(slot.start_date).toLocaleDateString("en-GB").replace(/\//g, "-") : "—"}</span>
                                  </div>
                                  <IoArrowForwardOutline className="text-gray-400" />
                                  <div className="flex items-center gap-2">
                                    <IoCalendarOutline className="text-red-600 dark:text-red-400" />
                                    <span>{slot.end_date ? new Date(slot.end_date).toLocaleDateString("en-GB").replace(/\//g, "-") : "—"}</span>
                                  </div>
                                </div>

                                {/* Weekly Schedule - Beautiful Grid with Icons */}
                                <div className="grid grid-cols-2 gap-3">
                                  {Object.entries(slot.days)
                                    .filter(([_, time]) => time !== "N/A")
                                    .map(([day, time]) => {
                                      const isToday = day === currentDay;
                                      const [start, end] = time.split("-").map(t => {
                                        const [h, m] = t.split(":").slice(0,2);
                                        const hour = parseInt(h, 10);
                                        const period = hour >= 12 ? "PM" : "AM";
                                        const adjHour = hour % 12 || 12;
                                        return `${adjHour}:${m} ${period}`;
                                      });
                                      const dayLabel = day.charAt(0).toUpperCase() + day.slice(1, 3);

                                      return (
                                        <div
                                          key={day}
                                          className={`flex flex-col items-center justify-center px-4 py-1 rounded-xl text-center transition-all duration-200 ${
                                            isToday
                                              ? "bg-blue-600 text-white shadow-lg scale-105"
                                              : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                          }`}
                                        >
                                          <span className="text-sm font-semibold">{dayLabel} day</span>
                                          <span className={`text-sm font-bold mt-1 ${isToday ? "text-white" : "text-inherit"}`}>
                                            {start} - {end}
                                          </span>
                                        </div>
                                      );
                                    })}
                                </div>

                                {/* Today's Class Highlight */}
                                {todayTimeRange && todayTimeRange !== "N/A" && (
                                  <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700 text-center">
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1">
                                      <IoSparklesOutline className="text-yellow-500" />
                                      Today's Session
                                    </p>
                                    <p className="text-lg font-bold text-blue-600 dark:text-blue-400 mt-1">
                                      {formatTimeRange(todayTimeRange)}
                                    </p>
                                  </div>
                                )}
                              </div>

                              {/* Hover Arrow */}
                              <IoChevronForwardOutline className="absolute bottom-4 right-5 text-2xl text-blue-500 dark:text-blue-400 opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-2 transition-all duration-300" />
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  )}

  {/* Pagination */}
  {!loading && filteredSchedule.length > 0 && (
    <div className="flex justify-center items-center mt-10 gap-3">
      <button
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`px-6 py-2.5 rounded-lg font-medium transition ${
          currentPage === 1
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-blue-600 text-white hover:bg-blue-700"
        }`}
      >
        Previous
      </button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
        <button
          key={page}
          onClick={() => handlePageChange(page)}
          className={`w-10 h-10 rounded-lg font-medium transition ${
            currentPage === page
              ? "bg-blue-700 text-white shadow-md"
              : "bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
          }`}
        >
          {page}
        </button>
      ))}
      <button
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`px-6 py-2.5 rounded-lg font-medium transition ${
          currentPage === totalPages
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-blue-600 text-white hover:bg-blue-700"
        }`}
      >
        Next
      </button>
    </div>
  )}
</div>
  );
};

export default InstructorCourseschedule;